import React, { useEffect, useRef, useState } from 'react';

import { listTpaBills, openTpaBillPdf } from '../../../services/api/admissionDischargeApi';
import { formatPaise } from '../../../utils/money';
import { tpaBillErrorMessage } from './billModel';
import { formatDateTimeIST } from '../../../utils/formatIST';

const LATEST_COUNT = 5;

/**
 * TpaBillSection (TPA-PREAUTH/tpa-bill · S1 · FE-2, stories.md Screen C) —
 * the issued-TPA-bills list on the admission, mounted beneath the insurance
 * card in `AdmissionWorkspace`'s Details tab. Columns: Bill no. · Issued on
 * (date + time IST) · Issued by · Total · per-row Reprint. Newest first —
 * server-ordered (`seq_no` DESC, C2 frozen contract), this component never
 * re-sorts. Latest 5 shown by default; "Show all (n)" reveals the rest.
 *
 * ABSENT before the first issue — no empty-state placeholder, no "No bills
 * yet" copy: the section renders nothing until `listTpaBills` returns at
 * least one row, exactly per AC1/DoD of S1 FE-2.
 *
 * Single fetch per mount (`fetchedRef`, same idiom as `FinalBillView`'s own
 * `getFinalBill` load in `RunningBillPanel.jsx`) — a re-render can't
 * multiply the request. Issuing a NEW bill elsewhere (the "TPA Bill" button
 * on `RunningBillPanel`, both views) never triggers a refetch here; this
 * section picks up the new row the next time it mounts (the caller gates
 * mounting on `payer_mode === CASHLESS`, so a non-cashless admission never
 * even issues this GET).
 */
export default function TpaBillSection({ admissionId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [reprintingSeq, setReprintingSeq] = useState(null);
  const [reprintError, setReprintError] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current || !admissionId) return;
    fetchedRef.current = true;
    (async () => {
      const res = await listTpaBills(admissionId);
      if (res.success) setItems(res.data?.items || []);
      setLoading(false);
    })();
  }, [admissionId]);

  // "TPA Bill" (issue, on RunningBillPanel) and "Reprint" (here, per row) are
  // DISTINCT actions — this handler only ever calls `openTpaBillPdf`, never
  // `issueTpaBill`. Single-flight guarded so a double-click on the same row
  // opens the viewer once.
  const handleReprint = async (seqNo) => {
    if (reprintingSeq != null) return;
    setReprintingSeq(seqNo);
    setReprintError(null);
    const res = await openTpaBillPdf(admissionId, seqNo);
    setReprintingSeq(null);
    if (!res.success) setReprintError(tpaBillErrorMessage(res.code) || res.error || 'Could not open the TPA bill.');
  };

  if (loading || items.length === 0) return null;

  const visible = showAll ? items : items.slice(0, LATEST_COUNT);

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4" data-testid="tpa-bill-section">
      <h4 className="text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">TPA bills issued</h4>
      {reprintError && (
        <div className="text-[12px] text-red-700 dark:text-red-300 mb-2" data-testid="tpa-bill-section-error">{reprintError}</div>
      )}
      <div className="flex flex-col">
        {visible.map((item) => (
          <div
            key={item.seq_no}
            className="flex items-center justify-between gap-3 text-[12.5px] py-1.5 border-b border-slate-100 dark:border-slate-700 last:border-0"
            data-testid={`tpa-bill-row-${item.seq_no}`}
          >
            <div className="flex flex-col">
              <span className="font-semibold text-slate-800 dark:text-slate-100">{item.bill_no_display}</span>
              {/* `issued_by_name` is nullable on the BUILT contract (a bill
                  issued by a member whose name is gone) — no dangling " · ". */}
              <span className="text-slate-500 dark:text-slate-400">
                {formatDateTimeIST(item.issued_at)}{item.issued_by_name ? ` · ${item.issued_by_name}` : ''}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="tabular-nums font-semibold text-slate-800 dark:text-slate-100">{formatPaise(item.total_paise)}</span>
              <button
                type="button"
                onClick={() => handleReprint(item.seq_no)}
                disabled={reprintingSeq === item.seq_no}
                className="px-2.5 py-1 text-[12px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
                data-testid={`tpa-bill-reprint-${item.seq_no}`}
              >{reprintingSeq === item.seq_no ? 'Opening…' : 'Reprint'}</button>
            </div>
          </div>
        ))}
      </div>
      {!showAll && items.length > LATEST_COUNT && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="w-full text-center mt-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-medium hover:bg-slate-100 dark:hover:bg-slate-700"
          data-testid="tpa-bill-show-all"
        >{`Show all (${items.length})`}</button>
      )}
    </div>
  );
}
